import React, { useEffect, useState, useMemo, useImperativeHandle, forwardRef } from "react";
import { getFeatureRegistry, getClientFeatures, setClientFeatures } from "../api";

// Per-client feature toggles backed by `feature_flags` (scope='client'). We
// enrich each registry row with icon + human copy so admins recognise what
// they're toggling instead of squinting at flag keys.
//
// Save contract: parent forwards a ref and calls `ref.current.save()` in its
// submit handler; component returns { saved: N } or throws.

/* ═══════════════ ICON PALETTE ═══════════════ */
const Icon = ({ path }) => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);
const P = {
  bookings:    "M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4",
  gads:        "M13 10V3L4 14h7v7l9-11h-7z",
  ga4:         "M3 3v17a1 1 0 001 1h17M8 17V9M13 17V5M18 17v-7",
  aim:         "M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z",
  events:      "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z",
  routes:      "M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7",
  competitors: "M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z",
  menu:        "M4 6h16M4 12h16M4 18h16",
  search:      "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
  generic:     "M13 10V3L4 14h7v7l9-11h-7z",
};

/* ═══════════════ PALETTE ═══════════════ */
const COLORS = {
  blue:    { bg: "bg-blue-50 dark:bg-blue-950/40",       fg: "text-blue-600 dark:text-blue-400",       on: "bg-blue-500" },
  red:     { bg: "bg-red-50 dark:bg-red-950/40",         fg: "text-red-600 dark:text-red-400",         on: "bg-red-500" },
  amber:   { bg: "bg-amber-50 dark:bg-amber-950/40",     fg: "text-amber-600 dark:text-amber-400",     on: "bg-amber-500" },
  teal:    { bg: "bg-teal-50 dark:bg-teal-950/40",       fg: "text-teal-600 dark:text-teal-400",       on: "bg-teal-500" },
  purple:  { bg: "bg-purple-50 dark:bg-purple-950/40",   fg: "text-purple-600 dark:text-purple-400",   on: "bg-purple-500" },
  emerald: { bg: "bg-emerald-50 dark:bg-emerald-950/40", fg: "text-emerald-600 dark:text-emerald-400", on: "bg-emerald-500" },
  slate:   { bg: "bg-slate-100 dark:bg-slate-800",       fg: "text-slate-600 dark:text-slate-300",     on: "bg-slate-500" },
};

/* ═══════════════ FLAG METADATA ═══════════════ */
// Keyed by flag_key. Missing keys fall back to registry description +
// generic slate styling. Same visual language as the dashboard tiles.
const FLAG_META = {
  "dashboard.booking":                 { title: "Booking Intelligence",       pitch: "Bookings, sessions, conversion rates, revenue.",                icon: P.bookings,    color: "blue",    category: "Dashboards" },
  "dashboard.google_ads":              { title: "Google Ads",                 pitch: "Track ROAS, spend, campaigns, and keywords.",                    icon: P.gads,        color: "red",     category: "Dashboards" },
  "dashboard.google_analytics":        { title: "Website Analytics",          pitch: "Traffic sources, page performance, engagement.",                 icon: P.ga4,         color: "amber",   category: "Dashboards" },
  "dashboard.aim":                     { title: "AI Visibility",              pitch: "How ChatGPT, Gemini, and Google AI describe this property.",     icon: P.aim,         color: "purple",  category: "Dashboards" },
  "dashboard.event_intelligence":      { title: "Events Intelligence",        pitch: "Local events that drive booking demand.",                        icon: P.events,      color: "emerald", category: "Dashboards" },
  "dashboard.route_intelligence":      { title: "Route Intelligence",         pitch: "Guest origins, corridors, and trip economics.",                  icon: P.routes,      color: "slate",   category: "Dashboards" },
  "dashboard.competitive_intelligence":{ title: "Competitive Intelligence",   pitch: "Benchmark rates against local competitors.",                     icon: P.competitors, color: "purple",  category: "Dashboards" },
  // The flag key `search_console` is backend contract and now matches the customer-facing
  // name as well. Keep the two in step; do not rename the key on its own.
  "dashboard.search_console":          { title: "Search Console",        pitch: "How people find this park in Google, and what to fix.",          icon: P.search,      color: "blue",    category: "Dashboards" },
  "feature.new_menu":                  { title: "New grouped sidebar",        pitch: "Show the redesigned grouped sidebar for this client.",           icon: P.menu,        color: "teal",    category: "Features" },
};

const CATEGORY_ORDER = ["Dashboards", "Features", "Other"];

/* ═══════════════ TOGGLE SWITCH ═══════════════ */
const Toggle = ({ enabled, onChange, colorClass = "bg-violet-500" }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    onClick={onChange}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#32B1FF] ${enabled ? colorClass : "bg-gray-300 dark:bg-gray-600"}`}
  >
    <span
      className={`inline-block h-5 w-5 rounded-full bg-white shadow transform transition-transform ${enabled ? "translate-x-5" : "translate-x-0.5"}`}
    />
  </button>
);

/* ═══════════════ FEATURE ROW ═══════════════ */
const FeatureRow = ({ flag, onToggle }) => {
  const meta = FLAG_META[flag.key] || null;
  const title = meta?.title || flag.description || flag.key;
  const pitch = meta?.pitch || (meta ? null : `Flag key: ${flag.key}`);
  const color = COLORS[meta?.color || "slate"];
  const iconPath = meta?.icon || P.generic;
  const isDirty = flag.enabled !== flag.initial;

  return (
    <div
      className={`flex items-start justify-between gap-3 p-3 rounded-lg border transition-colors ${
        isDirty
          ? "border-amber-300 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20"
          : "border-gray-200 bg-white hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:hover:bg-gray-800/40"
      }`}
    >
      <div className="flex items-start gap-3 min-w-0 flex-1">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${color.bg} ${color.fg}`}>
          <Icon path={iconPath} />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</p>
            {isDirty && (
              <span className="text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-300 bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 rounded">
                Unsaved
              </span>
            )}
          </div>
          {pitch && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">
              {pitch}
            </p>
          )}
        </div>
      </div>
      <Toggle enabled={flag.enabled} onChange={onToggle} colorClass={color.on} />
    </div>
  );
};

/* ═══════════════ SECTION HEADER ═══════════════ */
const SectionHeader = ({ label, subtitle, enabled, total, onEnableAll, onDisableAll, allEnabled, noneEnabled }) => (
  <div className="flex items-center justify-between mb-2">
    <div>
      <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
        {label}
      </h4>
      {subtitle && <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 tabular-nums">
        {enabled} / {total}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onEnableAll}
          disabled={allEnabled}
          className="text-[11px] px-1.5 py-0.5 rounded text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Enable all
        </button>
        <span className="text-gray-300 dark:text-gray-700">·</span>
        <button
          type="button"
          onClick={onDisableAll}
          disabled={noneEnabled}
          className="text-[11px] px-1.5 py-0.5 rounded text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Disable all
        </button>
      </div>
    </div>
  </div>
);

/* ═══════════════ MAIN COMPONENT ═══════════════ */
const ClientFeatureToggles = forwardRef(function ClientFeatureToggles({ clientId }, ref) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [flags, setFlags] = useState([]);

  useEffect(() => {
    if (!clientId) { setFlags([]); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const [regRes, clientRes] = await Promise.all([
          getFeatureRegistry(),
          getClientFeatures(clientId),
        ]);
        const registry = regRes?.data?.project_features || regRes?.data?.features || [];
        const effective = clientRes?.data?.features || {};
        const seen = new Map();
        for (const r of registry) {
          const key = r.flag_key || r.key;
          if (!key || seen.has(key)) continue;
          seen.set(key, {
            key,
            description: r.description || r.name || key,
            enabled: !!effective[key],
            initial: !!effective[key],
          });
        }
        if (!cancelled) setFlags(Array.from(seen.values()));
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to load feature flags");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  useImperativeHandle(ref, () => ({
    async save() {
      if (!clientId) return { saved: 0 };
      const diff = {};
      for (const f of flags) if (f.enabled !== f.initial) diff[f.key] = f.enabled;
      if (!Object.keys(diff).length) return { saved: 0 };
      setSaving(true);
      try {
        await setClientFeatures(clientId, diff);
        setFlags((prev) => prev.map((f) => ({ ...f, initial: f.enabled })));
        return { saved: Object.keys(diff).length };
      } finally {
        setSaving(false);
      }
    },
    hasChanges() {
      return flags.some((f) => f.enabled !== f.initial);
    },
  }), [clientId, flags]);

  // Group by category derived from FLAG_META
  const grouped = useMemo(() => {
    const buckets = new Map(CATEGORY_ORDER.map((c) => [c, []]));
    for (const f of flags) {
      const category = FLAG_META[f.key]?.category || "Other";
      if (!buckets.has(category)) buckets.set(category, []);
      buckets.get(category).push(f);
    }
    for (const arr of buckets.values()) {
      arr.sort((a, b) => {
        const ta = FLAG_META[a.key]?.title || a.description || a.key;
        const tb = FLAG_META[b.key]?.title || b.description || b.key;
        return ta.localeCompare(tb);
      });
    }
    return buckets;
  }, [flags]);

  const totalEnabled = flags.filter((f) => f.enabled).length;
  const dirtyCount = flags.filter((f) => f.enabled !== f.initial).length;

  const setAllInCategory = (category, enabled) => {
    setFlags((prev) => prev.map((f) => {
      const c = FLAG_META[f.key]?.category || "Other";
      return c === category ? { ...f, enabled } : f;
    }));
  };

  const toggle = (key) => {
    setFlags((prev) => prev.map((f) => (f.key === key ? { ...f, enabled: !f.enabled } : f)));
  };

  if (!clientId) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-4 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Save the client first — feature toggles are per-client and need an ID.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary bar */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Client Features</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Toggle what this property can see and use.
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums">
            {totalEnabled} <span className="text-gray-400 font-normal">of {flags.length}</span>
          </p>
          {dirtyCount > 0 ? (
            <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400">
              {dirtyCount} unsaved {dirtyCount === 1 ? "change" : "changes"}
            </p>
          ) : saving ? (
            <p className="text-[11px] text-gray-500 dark:text-gray-400">Saving…</p>
          ) : (
            <p className="text-[11px] text-gray-400 dark:text-gray-500">All saved</p>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-24">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500" />
        </div>
      ) : error ? (
        <div className="p-3 text-sm text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300 rounded-lg">
          {error}
        </div>
      ) : flags.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 italic text-center py-6">
          No feature flags registered.
        </p>
      ) : (
        <div className="space-y-6 max-h-[520px] overflow-y-auto pr-1">
          {CATEGORY_ORDER.concat(
            [...grouped.keys()].filter((c) => !CATEGORY_ORDER.includes(c))
          ).map((category) => {
            const list = grouped.get(category) || [];
            if (!list.length) return null;
            const enabled = list.filter((f) => f.enabled).length;
            return (
              <section key={category}>
                <SectionHeader
                  label={category}
                  subtitle={
                    category === "Dashboards" ? "Product dashboards this client can access." :
                    category === "Features"   ? "Optional platform features." :
                    null
                  }
                  enabled={enabled}
                  total={list.length}
                  allEnabled={enabled === list.length}
                  noneEnabled={enabled === 0}
                  onEnableAll={() => setAllInCategory(category, true)}
                  onDisableAll={() => setAllInCategory(category, false)}
                />
                <div className="space-y-2">
                  {list.map((f) => (
                    <FeatureRow key={f.key} flag={f} onToggle={() => toggle(f.key)} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
});

export default ClientFeatureToggles;
